import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { BankAccountService } from '../../services/bankAccount.service';
import { ComplianceService } from '../../services/compliance.service';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { FundingCheck } from '../../interfaces/compliance.interface';
import SearchableSelect from '../common/SearchableSelect';
import { money } from './complianceUtils';

interface Props {
    institutionId?: string;
    payrollIds: string[];
}

/** Advisory only: shows whether the chosen bank covers the selected net pay. Never blocks payment. */
const FundingCheckPanel: React.FC<Props> = ({ institutionId, payrollIds }) => {
    const [banks, setBanks] = useState<BankAccount[]>([]);
    const [bankId, setBankId] = useState('');
    const [result, setResult] = useState<FundingCheck | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [banksRefused, setBanksRefused] = useState(false);

    useEffect(() => {
        if (!institutionId) return;
        BankAccountService.listByInstitution(institutionId)
            .then(b => { setBanks(b); setBanksRefused(false); })
            .catch(() => { setBanks([]); setBanksRefused(true); });
    }, [institutionId]);
    useEffect(() => { setResult(null); setError(''); }, [bankId, payrollIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!institutionId) return null;

    const check = async () => {
        setBusy(true); setError('');
        try { setResult(await ComplianceService.fundingCheck(institutionId, banksRefused ? null : bankId, payrollIds)); }
        catch { setResult(null); setError('Funding check is unavailable for your access level.'); }
        finally { setBusy(false); }
    };

    return (
        <Box sx={{ mt: 2 }} data-testid="funding-check-panel" data-help-section="fundingCheck.panel">
            <Typography variant="subtitle2" gutterBottom>Funding check (advisory)</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                    <SearchableSelect<BankAccount> label="Paying bank account" size="small" options={banks} value={bankId} data-help="fundingCheck.field.bank"
                        getOptionLabel={b => `${b.bankName} - ${b.accountNumber}`} onChange={id => setBankId(id)} />
                </Box>
                <Button size="small" variant="outlined" disabled={(!bankId && !banksRefused) || busy || payrollIds.length === 0} data-help="fundingCheck.check" onClick={check}>Check funding</Button>
            </Box>
            {banksRefused && <Alert severity="info" sx={{ mt: 1 }}>The bank account list is not available to your role, so the check is run against the institution's BDT bank accounts and shows only OK or SHORT.</Alert>}
            {error && <Alert severity="info" sx={{ mt: 1 }}>{error}</Alert>}
            {result && (
                <Alert severity={result.status === 'OK' ? 'success' : 'warning'} sx={{ mt: 1 }}>
                    {result.status === 'OK' ? 'OK' : 'SHORT'}: required {money(result.required)}
                    {result.available != null ? `, available ${money(result.available)}` : ''}
                    {result.status === 'SHORT' && result.gap != null ? `, gap ${money(result.gap)}` : ''}. You can still record the payment.
                    {result.notes.map(n => <Typography key={n} variant="caption" display="block">{n}</Typography>)}
                </Alert>
            )}
        </Box>
    );
};

export default FundingCheckPanel;
